import React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, Illustration, toast } from '@node-space/ui-components'
import { ConnectionStatusTypes } from 'constants/bvnkNetwork'
import { useUpdateNetworkConnectionMutation } from 'hooks/mutations/BvnkNetwork/useUpdateNetworkConnectionMutation'
import { BvnkNetworkConnection, ConnectionStatusType } from 'types/bvnkNetwork'
import { getConnectionStatusUpdateSuccessMessage } from 'utils/bvnkNetwork'

export interface ConnectionConfirmationModalProps {
  connection: BvnkNetworkConnection
  connectionStatus: ConnectionStatusType
  onModalClose: () => void
}

export const ConnectionConfirmationModal = ({
  connection,
  connectionStatus,
  onModalClose,
}: ConnectionConfirmationModalProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { mutate: updateNetworkConnection, isPending: isUpdatingStatus } =
    useUpdateNetworkConnectionMutation()

  const updateConnection = (
    connection: BvnkNetworkConnection,
    connectionStatus: ConnectionStatusType
  ) => {
    updateNetworkConnection(
      { connectionId: connection?.id, connectionStatus },
      {
        onSuccess: () => {
          const successToastMessage = getConnectionStatusUpdateSuccessMessage(
            connection?.details?.name,
            connectionStatus,
            t
          )
          toast({
            header: successToastMessage,
            variant: connectionStatus === ConnectionStatusTypes.DISCONNECTED ? 'info' : 'success',
          })
          onModalClose?.()
          queryClient.invalidateQueries({ queryKey: ['bvnkNetworkConnections'] })
        },
        onError: () => {
          toast({
            header: t('bvnkNetwork.updateNetworkConnectionError', {
              networkMember: connection?.details?.name,
            }),
            variant: 'error',
          })
        },
      }
    )
  }

  const getConfirmationActions = () => {
    switch (connectionStatus) {
      case ConnectionStatusTypes.DISCONNECTED:
        return (
          <Button
            key="disconnect"
            variant="destructive"
            testId="connection-disconnect-button"
            loading={isUpdatingStatus}
            onClick={() => updateConnection(connection, connectionStatus)}
          >
            {t('bvnkNetwork.disconnect')}
          </Button>
        )
      case ConnectionStatusTypes.REJECTED:
        return (
          <Button
            key="denyRequest"
            variant="destructive"
            testId="connection-disconnect-button"
            loading={isUpdatingStatus}
            onClick={() => updateConnection(connection, connectionStatus)}
          >
            {t('bvnkNetwork.denyRequest')}
          </Button>
        )
      default:
        return <></>
    }
  }

  const getHeadline = () => {
    switch (connectionStatus) {
      case ConnectionStatusTypes.DISCONNECTED:
        return t('bvnkNetwork.disconnectModalHeadline', {
          networkMember: connection?.details?.name,
        })
      case ConnectionStatusTypes.REJECTED:
        return t('bvnkNetwork.denyRequestModalHeadline', {
          networkMember: connection?.details?.name,
        })
      default:
        return
    }
  }

  const getDescription = () => {
    switch (connectionStatus) {
      case ConnectionStatusTypes.DISCONNECTED:
        return t('bvnkNetwork.disconnectModalDescription', {
          networkMember: connection?.details?.name,
        })
      case ConnectionStatusTypes.REJECTED:
        return t('bvnkNetwork.denyRequestModalDescription', {
          networkMember: connection?.details?.name,
        })
      default:
        return
    }
  }

  const headline = getHeadline()
  const description = getDescription()

  return (
    <Dialog
      open
      onClose={onModalClose}
      title={headline}
      Illustration={<Illustration name="Prohibit" />}
      ActionButtons={[
        getConfirmationActions(),
        <Button
          key="cancel"
          testId="connection-cancel-button"
          onClick={onModalClose}
          variant="secondary"
        >
          {t('cancel')}
        </Button>,
      ]}
    >
      <p className="text-foreground-prominent text-body-regular text-center">{description}</p>
    </Dialog>
  )
}
